import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CatalogueFields, {
  type VehicleCatalogue,
} from "@/pages/vehicles/catalogue-fields";
import { errorText } from "./form-errors";
import { FormFields, FormSection } from "@/components/page-canvas";

export type VehicleValues = {
  make: string;
  model: string;
  fabrication_year: string | number | null;
  colour: string | null;
  registration_number: string | null;
  vin: string | null;
};
export const vehicleErrorTargets = (idPrefix = "vehicle") =>
  Object.fromEntries(
    [
      "make",
      "model",
      "fabrication_year",
      "colour",
      "registration_number",
      "vin",
    ].map((field) => [field, `${idPrefix}-${field}`]),
  );
export const vehicleTargets = vehicleErrorTargets();
export default function VehicleFields({
  values,
  catalogue,
  onChange,
  errors = {},
  inputPrefix = "vehicle",
  idPrefix = "vehicle",
  autoFocus = true,
  required = true,
}: {
  values: Partial<VehicleValues> | null;
  catalogue: VehicleCatalogue;
  onChange?: (field: keyof VehicleValues, value: string) => void;
  errors?: Record<string, unknown>;
  inputPrefix?: string;
  idPrefix?: string;
  autoFocus?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <FormSection>
      <p
        id={`${idPrefix}-catalogue-help`}
        className="text-sm text-muted-foreground"
      >
        {t("vehicles.manual_help")}
      </p>
      <CatalogueFields
        vehicle={values}
        catalogue={catalogue}
        errors={Object.fromEntries(
          Object.entries(errors).map(([field, value]) => [
            field,
            errorText(value),
          ]),
        )}
        onChange={onChange}
        inputPrefix={inputPrefix}
        idPrefix={idPrefix}
        autoFocus={autoFocus}
        required={required}
      />
      <FormFields>
        {(
          ["fabrication_year", "colour", "registration_number", "vin"] as const
        ).map((field) => (
          <div key={field} className="space-y-2">
            <Label htmlFor={`${idPrefix}-${field}`}>
              {t(`vehicles.${field}`)}
            </Label>
            <Input
              id={`${idPrefix}-${field}`}
              name={`${inputPrefix}[${field}]`}
              type={field === "fabrication_year" ? "number" : "text"}
              step={field === "fabrication_year" ? "1" : undefined}
              min={field === "fabrication_year" ? "1" : undefined}
              {...(onChange
                ? {
                    value: values?.[field] ?? "",
                    onChange: (event) => onChange(field, event.target.value),
                  }
                : { defaultValue: values?.[field] ?? "" })}
              aria-invalid={!!errors[field]}
              aria-describedby={
                errors[field] ? `${idPrefix}-${field}-error` : undefined
              }
            />
            {errors[field] ? (
              <p
                id={`${idPrefix}-${field}-error`}
                className="text-sm text-destructive"
              >
                {errorText(errors[field])}
              </p>
            ) : null}
          </div>
        ))}
      </FormFields>
    </FormSection>
  );
}
